<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { useConvexMutation } from 'convex-vue';
import IconLucideArrowLeft from '~icons/lucide/arrow-left';
import IconLucideArrowRight from '~icons/lucide/arrow-right';
import IconLucideChartColumnIncreasing from '~icons/lucide/chart-column-increasing';
import IconLucideCircleUserRound from '~icons/lucide/circle-user-round';
import IconLucideHand from '~icons/lucide/hand';
import IconLucideMusic2 from '~icons/lucide/music-2';
import IconLucideSmilePlus from '~icons/lucide/smile-plus';
import type { Id } from '../../convex/_generated/dataModel';
import { api } from '../../convex/_generated/api';
import {
  feedbackOptions,
  formatFeedbackDetail,
  normalizeFeedbackValue,
  type FeedbackRatingValue,
  type FeedbackToken,
} from '../features/feedback';
import { soundOptions } from '../features/oneTimeEvents';
import CMenu from './primitives/CMenu.vue';
import CMenuItem from './primitives/CMenuItem.vue';

type ViewerSignals = {
  emoji: string;
  feedback: string;
  hand: boolean;
  id: Id<'participants'>;
  role: 'student' | 'teacher' | 'recorder';
  status: string;
};

const emojiOptions = [
  { value: '🙂', label: 'Happy' },
  { value: '😭', label: 'Crying' },
  { value: '😵', label: 'Confused' },
  { value: '👏', label: 'Applause' },
  { value: '🤯', label: 'Mind Blown' },
  { value: '🫡', label: 'Salute' },
  { value: '💡', label: 'I get it!!' },
  { value: '🏳️', label: 'I give up.' },
  { value: '🥳', label: 'Celebration!' },
  { value: '🤣', label: 'LOL!!' },
  { value: '💀', label: 'Dead' },
] as const;

const statusOptions = [
  { value: '⏳', label: 'Away / AFK', testId: 'away' },
  { value: '🤔', label: 'Thinking', testId: 'thinking' },
  { value: '📝', label: 'Taking Notes', testId: 'taking-notes' },
  { value: '🤣', label: 'LOL!!', testId: 'lol' },
  { value: '🔇', label: "Can't Hear / Tech. Issue", testId: 'tech-issue' },
];

const props = defineProps<{
  activeSoundName: string | null;
  conferenceSlug: string;
  feedbackVisibility: boolean;
  viewer: ViewerSignals;
}>();

const emit = defineEmits<{
  (event: 'notice', payload: { detail?: string; label: string }): void;
}>();

const { mutate: setHandRaised } = useConvexMutation(api.participants.setHandRaised);
const { mutate: setEmoji } = useConvexMutation(api.participants.setEmoji);
const { mutate: setFeedback } = useConvexMutation(api.participants.setFeedback);
const { mutate: setUserStatus } = useConvexMutation(api.participants.setUserStatus);
const { mutate: setFeedbackVisibility } = useConvexMutation(api.conferences.setFeedbackVisibility);
const { mutate: playSoundEffect } = useConvexMutation(api.sounds.playSoundEffect);
const { mutate: stopSoundEffect } = useConvexMutation(api.sounds.stopSoundEffect);
const { mutate: publishFireworks } = useConvexMutation(api.celebrations.publishFireworks);

const showEmojiMenu = ref(false);
const showStatusMenu = ref(false);
const showFeedbackMenu = ref(false);
const showSoundMenu = ref(false);
const feedbackRating = ref(0);
const soundEffectUsage = useLocalStorage<Record<string, number>>('convex-rebuild.sound-effects', {});

watch(
  () => props.viewer.feedback,
  (value) => {
    feedbackRating.value = /^\d+$/u.test(value) ? Number.parseInt(value, 10) : 0;
  },
  { immediate: true },
);

const favouriteSounds = computed(() =>
  Object.entries(soundEffectUsage.value)
    .sort(([, left], [, right]) => right - left)
    .map(([soundId]) => soundId)
    .slice(0, 5),
);
const remainingSounds = computed(() => soundOptions.filter((option) => !favouriteSounds.value.includes(option.id)));
const signalTriggerColumns = computed(() => (props.viewer.role === 'teacher' ? 'grid-cols-5' : 'grid-cols-4'));
const viewerEmojiValue = computed(() => props.viewer.emoji || 'none');
const viewerStatusValue = computed(() => props.viewer.status || 'none');
const viewerFeedbackValue = computed(() => normalizeFeedbackValue(props.viewer.feedback) || 'none');

function formatStatusDetail(value: string): string {
  return statusOptions.find((option) => option.value === value)?.label ?? value;
}

async function runAction(label: string, action: () => Promise<unknown>, detail?: string, closeMenu?: () => void): Promise<void> {
  try {
    await action();
    closeMenu?.();
    emit('notice', { detail, label });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'The conference action could not be completed.',
      label,
    });
  }
}

function rememberSound(soundName: string): void {
  soundEffectUsage.value = {
    ...soundEffectUsage.value,
    [soundName]: (soundEffectUsage.value[soundName] ?? 0) + 1,
  };
}

async function toggleHandRaised(): Promise<void> {
  await runAction(
    props.viewer.hand ? 'Hand lowered' : 'Hand raised',
    async () =>
      await setHandRaised({
        conferenceSlug: props.conferenceSlug,
        participantId: props.viewer.id,
        value: !props.viewer.hand,
      }),
  );
}

async function chooseEmoji(value: string, close: () => void): Promise<void> {
  const nextValue = props.viewer.emoji === value ? '' : value;
  await runAction(
    nextValue ? 'Emoji updated' : 'Emoji cleared',
    async () =>
      await setEmoji({
        conferenceSlug: props.conferenceSlug,
        participantId: props.viewer.id,
        value: nextValue,
      }),
    nextValue || undefined,
    close,
  );
}

async function chooseStatus(value: string, close: () => void): Promise<void> {
  const nextValue = props.viewer.status === value ? '' : value;
  await runAction(
    nextValue ? 'Status updated' : 'Status cleared',
    async () =>
      await setUserStatus({
        conferenceSlug: props.conferenceSlug,
        participantId: props.viewer.id,
        value: nextValue,
      }),
    nextValue ? formatStatusDetail(nextValue) : undefined,
    close,
  );
}

async function chooseFeedback(value: FeedbackToken, close: () => void): Promise<void> {
  const nextValue = props.viewer.feedback === value ? '' : value;
  await runAction(
    nextValue ? 'Feedback updated' : 'Feedback cleared',
    async () =>
      await setFeedback({
        conferenceSlug: props.conferenceSlug,
        participantId: props.viewer.id,
        value: nextValue,
      }),
    nextValue ? formatFeedbackDetail(nextValue) : undefined,
    close,
  );
}

async function chooseFeedbackRating(close: () => void): Promise<void> {
  const ratingValue = String(feedbackRating.value) as FeedbackRatingValue;

  await runAction(
    'Feedback rating updated',
    async () =>
      await setFeedback({
        conferenceSlug: props.conferenceSlug,
        participantId: props.viewer.id,
        value: ratingValue,
      }),
    `${feedbackRating.value}/10`,
    close,
  );
}

async function playSound(soundName: string, close: () => void): Promise<void> {
  rememberSound(soundName);
  await runAction(
    'Sound effect queued',
    async () =>
      await playSoundEffect({
        conferenceSlug: props.conferenceSlug,
        soundName,
      }),
    soundOptions.find((option) => option.id === soundName)?.name ?? soundName,
    close,
  );
}

async function celebrateMistake(close: () => void): Promise<void> {
  await runAction(
    'Celebrate mistake triggered',
    async () =>
      await publishFireworks({
        conferenceSlug: props.conferenceSlug,
        text: 'A mistake we can all learn from!!',
      }),
    undefined,
    close,
  );
}

async function stopSound(close: () => void): Promise<void> {
  await runAction(
    'Sound effect stopped',
    async () =>
      await stopSoundEffect({
        conferenceSlug: props.conferenceSlug,
      }),
    undefined,
    close,
  );
}

async function toggleFeedbackVisibility(close: () => void): Promise<void> {
  const nextValue = !props.feedbackVisibility;
  await runAction(
    nextValue ? 'Feedback shown conference-wide' : 'Feedback hidden conference-wide',
    async () =>
      await setFeedbackVisibility({
        conferenceSlug: props.conferenceSlug,
        value: nextValue,
      }),
    undefined,
    close,
  );
}
</script>

<template>
  <section
    class="border-conference-shell-border bg-conference-shell text-conference-shell-foreground border-t px-1.5 py-1.5"
    data-testid="conference-signal-bar"
  >
    <div class="grid gap-1.5" :class="signalTriggerColumns">
      <button
        type="button"
        class="text-conference-control-idle hover:bg-conference-control-hover hover:text-conference-control-highlight focus-visible:ring-conference-accent/25 inline-flex h-8 w-full items-center justify-center rounded-xs transition focus-visible:ring-2 focus-visible:outline-none"
        :class="viewer.hand ? 'bg-conference-control-pressed text-conference-control-highlight' : ''"
        data-testid="conference-signal-hand"
        :aria-pressed="viewer.hand"
        aria-label="Raise hand"
        @click="toggleHandRaised"
      >
        <IconLucideHand aria-hidden="true" class="size-4.5" />
      </button>

      <CMenu v-model:open="showEmojiMenu" align="left" tone="light" width="lg" panel-test-id="conference-signal-emoji-menu">
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="text-conference-control-idle hover:bg-conference-control-hover hover:text-conference-control-highlight focus-visible:ring-conference-accent/25 inline-flex h-8 w-full items-center justify-center rounded-xs transition focus-visible:ring-2 focus-visible:outline-none"
            :class="open || viewer.emoji ? 'bg-conference-control-pressed text-conference-control-highlight' : ''"
            data-testid="conference-signal-emoji-trigger"
            :data-current-value="viewerEmojiValue"
            aria-label="Emoji status"
            @click="toggle"
          >
            <IconLucideSmilePlus aria-hidden="true" class="size-4.5" />
          </button>
        </template>

        <template #default="{ close }">
          <div class="grid w-75 grid-cols-2 gap-x-2 gap-y-px p-1">
            <CMenuItem
              v-for="option in emojiOptions"
              :key="option.value"
              :active="viewer.emoji === option.value"
              tone="light"
              @click="void chooseEmoji(option.value, close)"
            >
              {{ option.value }} {{ option.label }}
            </CMenuItem>
          </div>
        </template>
      </CMenu>

      <CMenu v-model:open="showStatusMenu" align="left" tone="light" width="lg" panel-test-id="conference-signal-status-menu">
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="text-conference-control-idle hover:bg-conference-control-hover hover:text-conference-control-highlight focus-visible:ring-conference-accent/25 inline-flex h-8 w-full items-center justify-center rounded-xs transition focus-visible:ring-2 focus-visible:outline-none"
            :class="open || viewer.status ? 'bg-conference-control-pressed text-conference-control-highlight' : ''"
            data-testid="conference-signal-status-trigger"
            :data-current-value="viewerStatusValue"
            aria-label="User status"
            @click="toggle"
          >
            <IconLucideCircleUserRound aria-hidden="true" class="size-4.5" />
          </button>
        </template>

        <template #default="{ close }">
          <div class="w-65 p-1">
            <CMenuItem
              v-for="option in statusOptions"
              :key="option.value"
              :active="viewer.status === option.value"
              :data-testid="`status-option-${option.testId}`"
              tone="light"
              @click="void chooseStatus(option.value, close)"
            >
              {{ option.value }} {{ option.label }}
            </CMenuItem>
          </div>
        </template>
      </CMenu>

      <CMenu v-model:open="showFeedbackMenu" align="left" tone="light" width="lg" panel-test-id="conference-signal-feedback-menu">
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="text-conference-control-idle hover:bg-conference-control-hover hover:text-conference-control-highlight focus-visible:ring-conference-accent/25 inline-flex h-8 w-full items-center justify-center rounded-xs transition focus-visible:ring-2 focus-visible:outline-none"
            :class="open || viewer.feedback ? 'bg-conference-control-pressed text-conference-control-highlight' : ''"
            data-testid="conference-signal-feedback-trigger"
            :data-current-value="viewerFeedbackValue"
            aria-label="Quick feedback"
            @click="toggle"
          >
            <IconLucideChartColumnIncreasing aria-hidden="true" class="size-4.5" />
          </button>
        </template>

        <template #default="{ close }">
          <div class="w-57.5 p-1">
            <CMenuItem
              v-for="option in feedbackOptions.slice(0, 3)"
              :key="option.value"
              :active="viewerFeedbackValue === option.value"
              tone="light"
              @click="void chooseFeedback(option.value, close)"
            >
              {{ option.display }} {{ option.label }}
            </CMenuItem>

            <div class="text-conference-ink flex items-center gap-2 px-2.5 py-1 text-xs">
              <button type="button" class="text-conference-accent-muted shrink-0 text-sm" @click="feedbackRating = 0">⭐</button>
              <span class="w-4 shrink-0 text-center font-semibold">{{ feedbackRating }}</span>
              <input
                v-model="feedbackRating"
                class="accent-conference-accent-deep flex-1"
                max="10"
                min="0"
                step="1"
                type="range"
                @change="void chooseFeedbackRating(close)"
              />
            </div>

            <div class="border-conference-line-soft my-1 border-t" />

            <CMenuItem
              v-for="option in feedbackOptions.slice(3)"
              :key="option.value"
              :active="viewerFeedbackValue === option.value"
              tone="light"
              @click="void chooseFeedback(option.value, close)"
            >
              <span class="inline-flex items-center gap-2">
                <IconLucideArrowRight v-if="option.value === 'faster'" aria-hidden="true" class="size-3.5" />
                <IconLucideArrowLeft v-else-if="option.value === 'slower'" aria-hidden="true" class="size-3.5" />
                <span>{{ option.label }}</span>
              </span>
            </CMenuItem>

            <div class="border-conference-line-soft my-1 border-t" />

            <button
              type="button"
              class="text-conference-ink hover:bg-conference-accent-soft hover:text-conference-accent-text flex w-full items-center justify-start px-2.5 py-1.25 text-left text-xs transition"
              @click="void toggleFeedbackVisibility(close)"
            >
              {{ feedbackVisibility ? 'Hide Feedback' : 'Show Feedback' }}
            </button>
          </div>
        </template>
      </CMenu>

      <CMenu
        v-if="viewer.role === 'teacher'"
        v-model:open="showSoundMenu"
        align="left"
        tone="light"
        width="lg"
        panel-test-id="conference-signal-sound-menu"
      >
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="text-conference-control-idle hover:bg-conference-control-hover hover:text-conference-control-highlight focus-visible:ring-conference-accent/25 inline-flex h-8 w-full items-center justify-center rounded-xs transition focus-visible:ring-2 focus-visible:outline-none"
            :class="open ? 'bg-conference-control-pressed text-conference-control-highlight' : ''"
            data-testid="conference-signal-sound-trigger"
            aria-label="Sound effects"
            @click="toggle"
          >
            <IconLucideMusic2 aria-hidden="true" class="size-4.5" />
          </button>
        </template>

        <template #default="{ close }">
          <div class="w-84 p-2">
            <p class="px-1 text-xs font-semibold tracking-[0.18em] text-slate-500 uppercase">Sound Effects</p>

            <div v-if="favouriteSounds.length > 0" class="mt-2 flex flex-wrap gap-1">
              <button
                v-for="soundId in favouriteSounds"
                :key="soundId"
                type="button"
                class="border-conference-accent-border bg-conference-accent text-conference-accent-text rounded-[1px] border px-2 py-1 text-xs font-semibold"
                @click="void playSound(soundId, close)"
              >
                {{ soundOptions.find((option) => option.id === soundId)?.name ?? soundId }}
              </button>
            </div>

            <div class="mt-2 grid grid-cols-4 gap-1">
              <button
                v-for="sound in remainingSounds"
                :key="sound.id"
                type="button"
                class="border-conference-line-soft bg-conference-surface-muted text-conference-ink hover:border-conference-accent-border hover:bg-conference-accent-soft rounded-[1px] border px-2 py-1 text-left text-xs font-semibold transition"
                @click="void playSound(sound.id, close)"
              >
                {{ sound.name }}
              </button>
            </div>

            <div class="border-conference-line-soft mt-2 flex gap-1 border-t pt-2">
              <button
                type="button"
                class="border-conference-danger-border bg-conference-danger-fill text-conference-danger-text inline-flex items-center gap-1 rounded-[1px] border px-2 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="!activeSoundName"
                @click="void stopSound(close)"
              >
                <span aria-hidden="true">■</span>
                Stop Sound
              </button>
              <button
                type="button"
                class="border-conference-dock-accent bg-conference-dock-subtle text-conference-dock-text inline-flex items-center gap-1 rounded-[1px] border px-2 py-1 text-xs font-semibold"
                @click="void celebrateMistake(close)"
              >
                <span aria-hidden="true">✦</span>
                Celebrate Mistake
              </button>
            </div>
          </div>
        </template>
      </CMenu>
    </div>
  </section>
</template>
